import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row } from "react-table";

import DateTimePicker from '../../Components/DateTimePicker';
import { ColumnProps, defaultColumnProps } from '../ColumnProps';
import { convertToDateTime, useDateUtils } from "../../Utilities/useDateUtils";

/**
 * @param header Translation key for header of the column
 * @param accessor The object's field that is being used for the column
 * @param canEdit Enables or disabled input field
 * @param width Specifies the width of the table that the column should take up, as a percetenage
 * @returns React-Table column definition for a column that has inputs
 */
function EditableDateColumn<T extends object>(header: string, accessor: keyof T, canEdit: boolean, props: ColumnProps = defaultColumnProps) {
    const { t } = useTranslation();

    return {
        Header: t<string>(header),
        id: header,
        accessor: accessor.toString(),
        Cell: (props: any) => <EditableDateCell {...props} accessor={accessor} canEdit={canEdit} />,
        disableFilters: true,
        className: "overflow-visible",
        ...props
    };
}

interface EditableCellProps<T extends object> {
    row: Row<T>,
    accessor: keyof T,
    value: any,
    canEdit: boolean,
    updateData: (accessor: keyof T, row: Row<T>, value: any) => void,
}

function EditableDateCell<T extends object>({ row, accessor, value: initialValue, canEdit, updateData }: EditableCellProps<T>) {
    const { convertUTCToShortDateTime } = useDateUtils();
    const [dateTime, setDateTime] = useState(convertToDateTime(initialValue));

    useEffect(() => {
        setDateTime(convertToDateTime(initialValue));
    }, [initialValue]);

    useEffect(() => {
        updateData(accessor, row, dateTime?.toUTC().toISO({ suppressMilliseconds: true }));
    }, [accessor, dateTime]);

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setShowCalendar(x => !x)
    }

    const [showCalendar, setShowCalendar] = useState(false);

    return <>
        <div onClick={handleClick}>{convertUTCToShortDateTime(dateTime)}</div>
        {showCalendar &&
            <DateTimePicker
            name={`table-datepicker-${accessor}-${row.index}`}
            dateTime={dateTime && new Date(dateTime.toISO({ includeOffset: false }) ?? "")}
            handleDateSelect={(date) => updateData(accessor, row, convertToDateTime(date)?.toUTC().toISO({ suppressMilliseconds: true }))}
            showIcon={false}
            inline={true}
            onClickOutside={()=>setShowCalendar(false)}
            />
        }
    </>;
}

export default EditableDateColumn;